import React, { useMemo } from 'react';
import Form from '@grnhse/seedling/lib/birch/components/forms/form';
import RediscoveryPageContent from 'talent_matching/talent_rediscovery/rediscovery_page_content';
import { RediscoveryTabContextProvider } from 'talent_matching/contexts/rediscovery_tab_context';
import noop from 'lodash/noop';
import {
  SavedSearch,
  SCORECARD_ATTRIBUTE_MATCH,
} from 'talent_search/shared/types';
import type {
  RediscoveryFormValues,
  RediscoveryPageDefaults,
} from 'talent_matching/talent_rediscovery/types';
import { DEFAULT_EXCLUDE_MATCH, RediscoveryProps } from 'talent_matching/types';
import { useUserContext } from 'talent_matching/contexts/user_context';
import { useLocalStorage } from 'react-use';
import AdvancedFilter from 'talent_matching/components/filters/advanced_filter';
import { extractRediscoveryFormFieldsFromSavedSearch } from 'talent_matching/components/filters/utils';

const PER_PAGE = 50;

function buildDefaultValues(
  userDateFormat: string,
  defaultFilters?: Partial<RediscoveryFormValues>
): RediscoveryPageDefaults {
  return {
    can_have: [],
    must_have: [],
    does_not_have: [],
    does_not_have_match: DEFAULT_EXCLUDE_MATCH,
    milestone: null,
    stages: [],
    include_rejection_reasons: [],
    exclude_rejection_reasons: [],
    scorecard_performance: null,
    scorecard_attribute_filters: [],
    scorecard_attribute_match: SCORECARD_ATTRIBUTE_MATCH.ANY,
    no_strong_no_scores: false,
    recently_active_in_my_greenhouse: false,
    exclude_hired: false,
    exclude_rejected: false,
    exclude_current_job_people: false,
    without_candidate_tags: [],
    appliedOnStart: null,
    appliedOnEnd: null,
    appliedOnType: null,
    jobs: [],
    departments: [],
    offices: [],
    advancedFilters: [new AdvancedFilter()],
    ...defaultFilters,
    user_date_format: userDateFormat,
    page: 1,
    perPage: PER_PAGE,
  };
}

const TalentRediscovery = (rediscoveryProps: RediscoveryProps) => {
  const { userDateFormat } = useUserContext();
  // The server emits one `initialFilterSet.formValues` regardless of source
  // (user saved search or Greenhouse preset). Running it through
  // `extractRediscoveryFormFieldsFromSavedSearch` is a no-op for native tier values and
  // does the necessary string→native normalization for SavedSearch params,
  // mirroring the manual Load path.
  const seededDefaultFilters = useMemo(() => {
    const formValues = rediscoveryProps.initialFilterSet?.formValues;
    if (!formValues) {
      return undefined;
    }
    return extractRediscoveryFormFieldsFromSavedSearch(
      { ...formValues } as unknown as SavedSearch,
      userDateFormat
    ) as unknown as Partial<RediscoveryFormValues>;
  }, [rediscoveryProps.initialFilterSet, userDateFormat]);
  const defaultValues = buildDefaultValues(
    userDateFormat,
    seededDefaultFilters
  );
  const clearedValues = buildDefaultValues(userDateFormat);

  const [activeSidePanelTab, setActiveSidePanelTab] = useLocalStorage<
    string | null
  >('talent_matching_rediscovery_panel_tab', null);

  return (
    <RediscoveryTabContextProvider
      rediscoveryProps={rediscoveryProps}
      activeSidePanelTab={activeSidePanelTab || null}
      setActiveSidePanelTab={setActiveSidePanelTab}
    >
      <Form defaultValues={defaultValues} onSubmit={noop} submitMode="preserve">
        <RediscoveryPageContent clearedValues={clearedValues} />
      </Form>
    </RediscoveryTabContextProvider>
  );
};

export default TalentRediscovery;
